import React, { useEffect, useRef, useState } from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';

type CommandItemType = {
  group: string;
  items: { key: string; value: string }[];
};

interface DMCommandProps {
  items: CommandItemType[];
  onClick: (value: string) => void;
  placeholder?: string;
  selectedTimeZoneValue: any;
}

const DMCommand: React.FC<DMCommandProps> = ({
  items,
  onClick,
  placeholder = 'Type to search...',
  selectedTimeZoneValue,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<string | null>(selectedTimeZoneValue);
  const commandRef = useRef<HTMLDivElement>(null);

  // Toggle command visibility
  const toggleCommand = () => {
    setIsOpen(prev => !prev);
  };

  // Close the dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (commandRef.current && !commandRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (key: string, value: string) => {
    setSelectedItem(key); // Set the selected item's key
    onClick(value); // Trigger the onClick callback with the selected item's value
    setIsOpen(false); // Close the dropdown
  };

  return (
    <div className='relative' ref={commandRef}>
      {/* Select Box */}
      <div
        onClick={toggleCommand}
        className='border border-brand-primary-border px-4 py-2 cursor-pointer bg-brand-secondary text-brand-silver rounded-md w-full'
      >
        {selectedItem || 'Select an option'}
      </div>
      {/* Command Dropdown */}
      {isOpen && (
        <div className='absolute mt-2 border rounded-md shadow-md w-full z-10'>
          <Command className='bg-brand-secondary border !border-brand-primary-border text-slate-300'>
            <CommandInput placeholder={placeholder} />
            <CommandList className='text-slate-300'>
              <CommandEmpty>No results found.</CommandEmpty>
              {items.map((group, index) => (
                <React.Fragment key={index}>
                  <CommandGroup heading={group.group}>
                    {group.items.map((item, itemIndex) => (
                      <CommandItem
                        className='text-slate-300'
                        key={itemIndex}
                        onSelect={() => handleSelect(item.key, item.value)}
                      >
                        {item.key}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  {index < items.length - 1 && <CommandSeparator />}
                </React.Fragment>
              ))}
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  );
};

export default DMCommand;
